import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarCheck, BookOpen, FileText, GraduationCap, Clock,
  Library, BarChart3, Bot, TrendingUp, TrendingDown,
  Users, Building2, AlertCircle, CheckCircle, ArrowRight,
  type LucideIcon,
} from 'lucide-react';
import { usePageTitle, Avatar } from '../components/AppShell';
import { useLang } from '../lib/lang';
import { apiGet } from '../lib/auth';
import type { AuthUser } from '../lib/auth';
import { T, STATUS_PILLS } from '../lib/tokens';
import { primaryRole } from '../lib/rbac';

// ── Design constants ──────────────────────────────────────────────────────────

const C = T.color;

// ── Reusable primitives ───────────────────────────────────────────────────────

function Card({ children, style, onClick }: {
  children:React.ReactNode; style?:React.CSSProperties; onClick?:()=>void;
}) {
  const [h, setH] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={()=>setH(true)}
      onMouseLeave={()=>setH(false)}
      style={{
        background:C.card, borderRadius:'10px',
        border:`1px solid ${h && onClick ? C.border : C.border}`,
        boxShadow: h && onClick ? T.shadow.md : T.shadow.sm,
        transition:'box-shadow 0.15s, transform 0.15s',
        transform: h && onClick ? 'translateY(-1px)' : 'none',
        cursor: onClick ? 'pointer' : 'default',
        overflow:'hidden',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function SectionTitle({ ms, en, lang }: { ms:string; en:string; lang:string }) {
  return (
    <h2 style={{
      fontFamily: T.font.serif, fontSize:'18px', fontWeight:600,
      color:C.textPrimary, marginBottom:'16px', marginTop:'32px',
      paddingBottom:'10px', borderBottom:`2px solid ${C.border}`,
      letterSpacing:'-0.01em',
    }}>
      {lang==='en' ? en : ms}
    </h2>
  );
}

function StatusPill({ status, lang }: { status:string; lang:string }) {
  const map: Record<string,{bg:string;color:string;ms:string;en:string}> = {
    present:   { bg:C.successLight, color:C.success, ms:'Hadir', en:'Present' },
    absent:    { bg:C.dangerLight,  color:C.danger,  ms:'Tidak Hadir', en:'Absent' },
    late:      { bg:C.warningLight, color:C.warning, ms:'Lewat', en:'Late' },
  };
  const cfg = map[status] ?? map['present']!;
  return (
    <span style={{
      background:cfg.bg, color:cfg.color,
      borderRadius:'999px', padding:'2px 10px',
      fontSize:'11px', fontWeight:600,
      display:'inline-block',
    }}>
      {lang==='en' ? cfg.en : cfg.ms}
    </span>
  );
}

// ── Skeleton cards ────────────────────────────────────────────────────────────

function StatSkeleton() {
  return (
    <Card style={{ padding:'20px' }}>
      <div className="skel" style={{ height:'12px', width:'60%', marginBottom:'12px' }} />
      <div className="skel" style={{ height:'32px', width:'40%', marginBottom:'8px' }} />
      <div className="skel" style={{ height:'10px', width:'80%' }} />
    </Card>
  );
}

// ── MOE / Super Admin Dashboard ───────────────────────────────────────────────

function MOEDashboard({ lang }: { lang:string }) {
  const E = lang==='en';
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const kpis = [
    { ms:'Hadir Hari Ini',     en:"Today's Attendance",  value:'76,234', delta:'+1.2%', trend:'up',  icon:CalendarCheck, color:C.success },
    { ms:'Sekolah Aktif',      en:'Active Schools',       value:'147/200', delta:null,   trend:null,  icon:Building2,     color:C.info },
    { ms:'Permohonan Tertangguh', en:'Pending Applications', value:'23',   delta:'-5',   trend:'down',icon:FileText,      color:C.warning },
    { ms:'Pendidik Aktif',     en:'Active Educators',     value:'7,891',  delta:'+12',  trend:'up',  icon:GraduationCap, color:C.success },
  ];

  const districts = [
    { name:'Brunei-Muara', pct:92.1, students:22480, color:'#0D7A4E' },
    { name:'Belait',       pct:89.8, students:13680, color:'#D97706' },
    { name:'Tutong',       pct:90.5, students:11240, color:'#D97706' },
    { name:'Temburong',    pct:94.2, students:5440,  color:'#0D7A4E' },
  ];

  const alerts = [
    { icon:AlertCircle, color:C.danger,  bg:C.dangerLight,  ms:'12 sekolah di bawah kehadiran 75%', en:'12 schools below 75% attendance' },
    { icon:FileText,    color:C.warning, bg:C.warningLight, ms:'23 permohonan enrolmen tertangguh',  en:'23 enrolment applications pending' },
    { icon:CheckCircle, color:C.success, bg:C.successLight, ms:'156 rekod CPD melebihi 100 jam',    en:'156 CPD records exceeded 100 hours' },
  ];

  useEffect(()=>{ setTimeout(()=>setLoading(false), 600); },[]);

  if (loading) return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'16px' }}>
      {[1,2,3,4].map(i=><StatSkeleton key={i} />)}
    </div>
  );

  return (
    <>
      {/* KPI tiles */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:'16px' }}>
        {kpis.map((k)=>(
          <Card key={k.en} style={{ padding:'20px' }}>
            <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
              <div>
                <p style={{ fontSize:'12px', fontWeight:500, color:C.textSecondary, marginBottom:'8px', letterSpacing:'0.02em' }}>
                  {E ? k.en : k.ms}
                </p>
                <p style={{ fontFamily:T.font.serif, fontSize:'28px', fontWeight:700, color:C.textPrimary, lineHeight:1, marginBottom:'8px' }}>
                  {k.value}
                </p>
                {k.delta && (
                  <div style={{ display:'flex', alignItems:'center', gap:'4px' }}>
                    {k.trend==='up' ? <TrendingUp size={12} color={C.success}/> : <TrendingDown size={12} color={C.danger}/>}
                    <span style={{ fontSize:'11px', fontWeight:600, color:k.trend==='up'?C.success:C.danger }}>
                      {k.delta}
                    </span>
                    <span style={{ fontSize:'11px', color:C.textDisabled }}>{E?'vs yesterday':'berbanding semalam'}</span>
                  </div>
                )}
              </div>
              <div style={{
                width:'40px', height:'40px', borderRadius:'10px',
                background:`${k.color}15`,
                display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
              }}>
                <k.icon size={20} color={k.color} strokeWidth={1.8}/>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <SectionTitle ms="Prestasi Kehadiran Mengikut Daerah" en="Attendance by District" lang={lang} />

      {/* District breakdown */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:'12px' }}>
        {districts.map((d)=>(
          <Card key={d.name} style={{ padding:'20px' }}>
            <p style={{ fontSize:'13px', fontWeight:600, color:C.textPrimary, marginBottom:'12px' }}>{d.name}</p>
            <p style={{ fontFamily:T.font.serif, fontSize:'32px', fontWeight:700, color:d.color, lineHeight:1, marginBottom:'8px' }}>
              {d.pct}%
            </p>
            <div style={{ height:'6px', background:C.borderSubtle, borderRadius:'999px', overflow:'hidden', marginBottom:'8px' }}>
              <div style={{ height:'100%', width:`${d.pct}%`, background:d.color, borderRadius:'999px', transition:'width 0.4s ease' }}/>
            </div>
            <p style={{ fontSize:'11px', color:C.textSecondary }}>
              {d.students.toLocaleString(E?'en-GB':'ms-BN')} {E?'students':'pelajar'}
            </p>
          </Card>
        ))}
      </div>

      <SectionTitle ms="Tindakan Diperlukan" en="Action Required" lang={lang} />
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))', gap:'12px' }}>
        {alerts.map((a,i)=>(
          <div key={i} style={{
            background:a.bg, borderRadius:'10px', padding:'16px',
            display:'flex', alignItems:'flex-start', gap:'12px',
            border:`1px solid ${a.color}22`,
          }}>
            <a.icon size={18} color={a.color} strokeWidth={2} style={{ flexShrink:0, marginTop:'1px' }}/>
            <p style={{ margin:0, fontSize:'13px', color:C.textPrimary, lineHeight:1.5 }}>
              {E ? a.en : a.ms}
            </p>
          </div>
        ))}
      </div>

      {/* EduBot CTA */}
      <div style={{ marginTop:'32px' }}>
        <div
          onClick={()=>navigate('/admin/edubot')}
          style={{
            background:`linear-gradient(135deg, ${C.midnight} 0%, ${C.charcoal} 100%)`,
            borderRadius:'12px', padding:'20px 24px',
            display:'flex', alignItems:'center', gap:'16px',
            cursor:'pointer', transition:'transform 0.15s',
          }}
          onMouseEnter={(e)=>(e.currentTarget.style.transform='translateY(-1px)')}
          onMouseLeave={(e)=>(e.currentTarget.style.transform='none')}
        >
          <div style={{ width:'44px', height:'44px', borderRadius:'10px', background:C.royal, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <Bot size={22} color={C.midnight} strokeWidth={2}/>
          </div>
          <div style={{ flex:1 }}>
            <p style={{ fontFamily:T.font.serif, fontSize:'15px', fontWeight:700, color:'#FFFFFF', marginBottom:'3px' }}>
              EduBot — {E?'AI Assistant':'Pembantu AI'}
            </p>
            <p style={{ fontSize:'12px', color:'#6B7280' }}>
              {E?'Ask anything about attendance, grades, or school-wide statistics':'Tanya tentang kehadiran, gred, atau statistik sekolah'}
            </p>
          </div>
          <ArrowRight size={18} color={C.royal}/>
        </div>
      </div>
    </>
  );
}

// ── School Admin Dashboard ────────────────────────────────────────────────────

function SchoolAdminDashboard({ lang, navigate }: { lang:string; navigate:(p:string)=>void }) {
  const E = lang==='en';

  const attendance = [
    { label:E?'Form 1A':'Tingkatan 1A', present:40, total:42, pct:95 },
    { label:E?'Form 2A':'Tingkatan 2A', present:38, total:44, pct:86 },
    { label:E?'Form 3A':'Tingkatan 3A', present:42, total:46, pct:91 },
    { label:E?'Form 4A':'Tingkatan 4A', present:45, total:48, pct:94 },
    { label:E?'Form 4B':'Tingkatan 4B', present:36, total:44, pct:82 },
    { label:E?'Form 5A':'Tingkatan 5A', present:41, total:46, pct:89 },
    { label:E?'Form 5B':'Tingkatan 5B', present:40, total:42, pct:95 },
  ];

  const pendingItems = [
    { ms:'Permohonan Enrolmen', en:'Enrolment Applications', count:6, path:'/admin/applications', color:C.warning, icon:FileText },
    { ms:'Pelajar Tidak Hadir 3+ hari', en:'Students absent 3+ days', count:2, path:'/admin/attendance/alerts', color:C.danger, icon:AlertCircle },
    { ms:'Laporan CPD Belum Selesai', en:'CPD reports pending', count:4, path:'/admin/cpd', color:C.info, icon:GraduationCap },
  ];

  const quickActions = [
    { ms:'Rekod Kehadiran', en:'Mark Attendance', path:'/admin/attendance', Icon:CalendarCheck, color:C.success },
    { ms:'Pendaftaran Baru', en:'New Enrolment',  path:'/admin/applications', Icon:FileText, color:C.info },
    { ms:'Lihat Laporan',   en:'View Reports',   path:'/admin/reports', Icon:BarChart3, color:C.warning },
    { ms:'Jadual Waktu',    en:'Timetable',      path:'/admin/timetable', Icon:Clock, color:'#7C3AED' },
  ];

  const totalPresent = attendance.reduce((s,r)=>s+r.present, 0);
  const totalStudents = attendance.reduce((s,r)=>s+r.total, 0);
  const overallPct = Math.round((totalPresent/totalStudents)*100);

  return (
    <>
      {/* Attendance banner */}
      <Card style={{ padding:'24px', marginBottom:'0' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'16px', flexWrap:'wrap', gap:'12px' }}>
          <div>
            <p style={{ fontSize:'12px', fontWeight:600, color:C.textSecondary, letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:'4px' }}>
              {E?"Today's School Attendance":'Kehadiran Sekolah Hari Ini'}
            </p>
            <div style={{ display:'flex', alignItems:'baseline', gap:'8px' }}>
              <span style={{ fontFamily:T.font.serif, fontSize:'40px', fontWeight:700, color:overallPct>=90?C.success:overallPct>=80?C.warning:C.danger, lineHeight:1 }}>
                {overallPct}%
              </span>
              <span style={{ fontSize:'13px', color:C.textSecondary }}>
                {totalPresent} / {totalStudents} {E?'students':'pelajar'}
              </span>
            </div>
          </div>
          <button
            onClick={()=>navigate('/admin/attendance')}
            style={{
              background:C.royal, color:C.midnight, border:'none',
              borderRadius:'8px', padding:'10px 20px',
              fontSize:'13px', fontWeight:700, cursor:'pointer',
              fontFamily:T.font.sans, display:'flex', alignItems:'center', gap:'6px',
            }}
          >
            <CalendarCheck size={15}/> {E?'Mark Attendance':'Tandakan Kehadiran'}
          </button>
        </div>
        {/* Class-by-class bars */}
        <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
          {attendance.map((row)=>(
            <div key={row.label} style={{ display:'flex', alignItems:'center', gap:'10px' }}>
              <span style={{ fontSize:'12px', color:C.textSecondary, minWidth:'90px', fontWeight:500 }}>{row.label}</span>
              <div style={{ flex:1, height:'8px', background:C.borderSubtle, borderRadius:'999px', overflow:'hidden' }}>
                <div style={{
                  height:'100%', width:`${row.pct}%`,
                  background:row.pct>=90?C.success:row.pct>=80?C.warning:C.danger,
                  borderRadius:'999px', transition:'width 0.4s ease',
                }}/>
              </div>
              <span style={{ fontSize:'12px', fontWeight:600, color:row.pct>=90?C.success:row.pct>=80?C.warning:C.danger, width:'36px', textAlign:'right' }}>
                {row.pct}%
              </span>
            </div>
          ))}
        </div>
      </Card>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'24px', marginTop:'24px' }}>
        {/* Pending actions */}
        <div>
          <SectionTitle ms="Tindakan Diperlukan" en="Action Required" lang={lang} />
          <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
            {pendingItems.map((item)=>(
              <Card key={item.en} onClick={()=>navigate(item.path)} style={{ padding:'16px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                  <div style={{ width:'36px', height:'36px', borderRadius:'8px', background:`${item.color}15`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <item.icon size={16} color={item.color} strokeWidth={2}/>
                  </div>
                  <span style={{ flex:1, fontSize:'13px', fontWeight:500, color:C.textPrimary }}>{E?item.en:item.ms}</span>
                  <span style={{
                    background:item.color, color:'#FFFFFF',
                    borderRadius:'999px', padding:'2px 9px',
                    fontSize:'11px', fontWeight:700, flexShrink:0,
                  }}>{item.count}</span>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Quick actions */}
        <div>
          <SectionTitle ms="Tindakan Pantas" en="Quick Actions" lang={lang} />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
            {quickActions.map((a)=>(
              <Card key={a.en} onClick={()=>navigate(a.path)} style={{ padding:'16px', textAlign:'center' }}>
                <div style={{ width:'40px', height:'40px', borderRadius:'10px', background:`${a.color}15`, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 10px' }}>
                  <a.Icon size={18} color={a.color} strokeWidth={1.8}/>
                </div>
                <p style={{ fontSize:'12px', fontWeight:600, color:C.textPrimary }}>{E?a.en:a.ms}</p>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

// ── Teacher Dashboard ─────────────────────────────────────────────────────────

function TeacherDashboard({ lang, navigate }: { lang:string; navigate:(p:string)=>void }) {
  const E = lang==='en';
  const now = new Date();
  const dayNames = E
    ? ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']
    : ['Ahad','Isnin','Selasa','Rabu','Khamis','Jumaat','Sabtu'];
  const dayName = dayNames[now.getDay()] ?? '';
  const hour = now.getHours();
  const currentTime = hour * 60 + now.getMinutes();

  const classes = [
    { time:'07:30', timeEnd:'08:30', subject:E?'Mathematics':'Matematik',        class:E?'Form 4A':'T4A', room:'B12',   done:currentTime>510 },
    { time:'09:00', timeEnd:'10:00', subject:E?'Science':'Sains',                class:E?'Form 3B':'T3B', room:'Lab 2', done:currentTime>600 },
    { time:'10:30', timeEnd:'11:30', subject:E?'Mathematics':'Matematik',        class:E?'Form 5A':'T5A', room:'B14',   done:currentTime>690 },
    { time:'13:00', timeEnd:'14:00', subject:E?'Add. Mathematics':'Mat. Tambahan',class:E?'Form 5B':'T5B', room:'B15',   done:false },
  ];

  const isCurrent = (t:string,te:string) => {
    const [h,m] = t.split(':').map(Number);
    const [eh,em] = te.split(':').map(Number);
    const start = (h??0)*60+(m??0); const end = (eh??0)*60+(em??0);
    return currentTime>=start && currentTime<=end;
  };

  const attentionStudents = [
    { name:'Muhammad Hafizuddin bin Hj Rosli', class:'T4A', absences:3 },
    { name:'Nurul Hidayah bte Hj Rashid',     class:'T3A', absences:3 },
  ];

  const pending = [
    { ms:'Gred T4A — Matematik (Ujian 2)', en:'Form 4A — Mathematics Test 2 grades', due:E?'Due today':'Hari ini' },
    { ms:'Gred T5A — Matematik Tambahan',  en:'Form 5A — Additional Maths grades',   due:E?'Due tomorrow':'Esok' },
  ];

  return (
    <>
      {/* Today's classes timeline */}
      <div style={{ marginBottom:'24px' }}>
        <h2 style={{ fontFamily:T.font.serif, fontSize:'20px', fontWeight:600, color:C.textPrimary, marginBottom:'16px' }}>
          {E?`My ${dayName} Classes`:`Kelas ${dayName} Saya`}
        </h2>
        <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
          {classes.map((cls,i)=>{
            const current = isCurrent(cls.time, cls.timeEnd);
            return (
              <Card key={i} style={{ padding:'16px', borderLeft: current ? `3px solid ${C.success}` : cls.done ? `3px solid ${C.border}` : `3px solid ${C.royal}` }}>
                <div style={{ display:'flex', alignItems:'center', gap:'16px' }}>
                  <div style={{ textAlign:'center', minWidth:'52px' }}>
                    <div style={{
                      fontFamily:T.font.mono, fontSize:'13px', fontWeight:600,
                      color: current ? C.success : cls.done ? C.textDisabled : C.textPrimary,
                      lineHeight:1.3,
                    }}>
                      {cls.time}
                    </div>
                    <div style={{ fontSize:'10px', color:C.textDisabled }}>–{cls.timeEnd.slice(0,5)}</div>
                  </div>

                  <div style={{ flex:1, minWidth:0 }}>
                    <p style={{ fontSize:'15px', fontWeight:700, color:cls.done?C.textDisabled:C.textPrimary, marginBottom:'2px' }}>
                      {cls.subject}
                    </p>
                    <p style={{ fontSize:'12px', color:C.textSecondary }}>
                      {cls.class} · {E?'Room':'Bilik'} {cls.room}
                      {current && (
                        <span style={{ marginLeft:'8px', background:'#ECFDF5', color:C.success, padding:'1px 8px', borderRadius:'999px', fontSize:'10px', fontWeight:700 }}>
                          {E?'● IN PROGRESS':'● BERLANGSUNG'}
                        </span>
                      )}
                    </p>
                  </div>

                  {cls.done ? (
                    <span style={{ background:C.successLight, color:C.success, padding:'4px 12px', borderRadius:'999px', fontSize:'11px', fontWeight:600, flexShrink:0 }}>
                      ✓ {E?'Done':'Selesai'}
                    </span>
                  ) : (
                    <button
                      onClick={()=>navigate('/admin/attendance')}
                      style={{
                        background:current?C.success:C.royal,
                        color:current?'#FFFFFF':C.midnight,
                        border:'none', borderRadius:'8px',
                        padding:'8px 14px', fontSize:'12px', fontWeight:700,
                        cursor:'pointer', whiteSpace:'nowrap', flexShrink:0,
                        fontFamily:T.font.sans,
                        transition:'transform 0.1s',
                      }}
                    >
                      {E?'Mark':'Tandai'}
                    </button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'24px' }}>
        {/* Students needing attention */}
        <div>
          <SectionTitle ms="Pelajar Perlu Perhatian" en="Students Needing Attention" lang={lang} />
          {attentionStudents.length===0 ? (
            <div style={{ background:C.successLight, borderRadius:'10px', padding:'20px', textAlign:'center' }}>
              <p style={{ fontSize:'13px', color:C.success, fontWeight:500 }}>✓ {E?'All students attending regularly':'Semua pelajar hadir secara teratur'}</p>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
              {attentionStudents.map((s,i)=>(
                <Card key={i} style={{ padding:'14px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                    <Avatar email={s.name} size={36}/>
                    <div style={{ flex:1, minWidth:0 }}>
                      <p style={{ fontSize:'13px', fontWeight:600, color:C.textPrimary, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{s.name}</p>
                      <p style={{ fontSize:'11px', color:C.textSecondary }}>{s.class}</p>
                    </div>
                    <span style={{ background:C.dangerLight, color:C.danger, borderRadius:'999px', padding:'2px 9px', fontSize:'11px', fontWeight:700, flexShrink:0 }}>
                      {s.absences} {E?'absent':'tidak hadir'}
                    </span>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Pending grade entries */}
        <div>
          <SectionTitle ms="Gred Belum Dihantar" en="Pending Grade Entry" lang={lang} />
          {pending.length===0 ? (
            <div style={{ background:C.successLight, borderRadius:'10px', padding:'20px', textAlign:'center' }}>
              <p style={{ fontSize:'13px', color:C.success, fontWeight:500 }}>✓ {E?'All grades submitted':'Semua gred telah dihantar'}</p>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
              {pending.map((p,i)=>(
                <Card key={i} onClick={()=>navigate('/admin/grades')} style={{ padding:'14px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div>
                      <p style={{ fontSize:'13px', fontWeight:500, color:C.textPrimary, marginBottom:'2px' }}>{E?p.en:p.ms}</p>
                      <span style={{ background:C.dangerLight, color:C.danger, borderRadius:'999px', padding:'1px 8px', fontSize:'10px', fontWeight:600 }}>
                        {p.due}
                      </span>
                    </div>
                    <BookOpen size={16} color={C.textDisabled}/>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ── Parent Dashboard ──────────────────────────────────────────────────────────

function ParentDashboard({ lang, navigate }: { lang:string; navigate:(p:string)=>void }) {
  const E = lang==='en';
  const [selectedChild, setSelectedChild] = useState(0);

  const children = [
    {
      name:'Ahmad Fahmi bin Abdullah', form:E?'Form 4A':'Tingkatan 4A',
      school:'SMAS Sayyidina Ali', status:'present', attendancePct:94,
      grades:[
        { subject:E?'Mathematics':'Matematik', grade:'A', score:88, trend:'up' as const },
        { subject:E?'Science':'Sains', grade:'B+', score:76, trend:'down' as const },
        { subject:E?'English':'Bahasa Inggeris', grade:'A+', score:95, trend:'up' as const },
      ],
      nextClass:{ subject:E?'Mathematics':'Matematik', time:'10:30', room:'B12' },
    },
    {
      name:'Siti Nooraina bte Abdullah', form:E?'Form 2B':'Tingkatan 2B',
      school:'SMAS Sayyidina Ali', status:'present', attendancePct:97,
      grades:[
        { subject:E?'Malay Language':'Bahasa Melayu', grade:'A', score:91, trend:'up' as const },
        { subject:E?'Mathematics':'Matematik', grade:'A+', score:96, trend:'up' as const },
      ],
      nextClass:{ subject:E?'Malay Language':'Bahasa Melayu', time:'13:00', room:'A3' },
    },
  ];

  const child = children[selectedChild]!;
  const isPresent = child.status==='present';

  return (
    <>
      {/* Child selector */}
      {children.length>1 && (
        <div style={{ display:'flex', gap:'8px', marginBottom:'20px', flexWrap:'wrap' }}>
          {children.map((c,i)=>(
            <button
              key={i}
              onClick={()=>setSelectedChild(i)}
              style={{
                display:'flex', alignItems:'center', gap:'8px',
                padding:'8px 16px',
                background: selectedChild===i ? C.royal : C.card,
                color: selectedChild===i ? C.midnight : C.textPrimary,
                border:`1.5px solid ${selectedChild===i ? C.royal : C.border}`,
                borderRadius:'999px', cursor:'pointer',
                fontFamily:T.font.sans, fontWeight:600, fontSize:'13px',
                transition:'all 0.15s',
              }}
            >
              <Avatar email={c.name} size={24}/>
              {c.name.split(' ')[0]}
              <span style={{
                width:'6px', height:'6px', borderRadius:'50%',
                background: c.status==='present' ? C.success : C.danger,
                flexShrink:0,
              }}/>
            </button>
          ))}
        </div>
      )}

      {/* Child status — the PRIMARY element */}
      <Card style={{ padding:'28px', marginBottom:'20px', borderLeft:`4px solid ${isPresent?C.success:C.warning}` }}>
        <div style={{ display:'flex', alignItems:'center', gap:'20px', flexWrap:'wrap' }}>
          <Avatar email={child.name} size={64}/>
          <div style={{ flex:1 }}>
            <h1 style={{ fontFamily:T.font.serif, fontSize:'22px', fontWeight:700, color:C.textPrimary, marginBottom:'4px' }}>
              {child.name.split(' ')[0]}
            </h1>
            <p style={{ fontSize:'13px', color:C.textSecondary, marginBottom:'12px' }}>
              {child.form} · {child.school}
            </p>
            <div style={{
              display:'inline-flex', alignItems:'center', gap:'8px',
              background: isPresent ? C.successLight : C.warningLight,
              color: isPresent ? C.success : C.warning,
              borderRadius:'999px', padding:'6px 16px',
              fontSize:'14px', fontWeight:700,
            }}>
              {isPresent ? '✓' : '○'}
              {isPresent
                ? (E?'Present today':'Hadir hari ini')
                : (E?'Not yet marked':'Belum ditandakan')}
            </div>
          </div>
          {/* Attendance ring */}
          <div style={{ textAlign:'center', flexShrink:0 }}>
            <div style={{ fontFamily:T.font.serif, fontSize:'36px', fontWeight:700, color:child.attendancePct>=90?C.success:C.warning, lineHeight:1 }}>
              {child.attendancePct}%
            </div>
            <div style={{ fontSize:'11px', color:C.textSecondary, marginTop:'2px' }}>
              {E?'Attendance rate':'Kadar kehadiran'}
            </div>
          </div>
        </div>
      </Card>

      {/* 3 info cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:'16px' }}>
        {/* Latest grade */}
        <Card style={{ padding:'20px' }}>
          <p style={{ fontSize:'11px', fontWeight:600, color:C.textSecondary, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'12px' }}>
            {E?'Latest Result':'Keputusan Terbaru'}
          </p>
          {child.grades.slice(0,1).map((g,i)=>(
            <div key={i}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:'14px', fontWeight:500, color:C.textPrimary }}>{g.subject}</span>
                <span style={{
                  fontFamily:T.font.serif, fontSize:'24px', fontWeight:700,
                  color: g.grade.startsWith('A') ? C.success : g.grade.startsWith('B') ? C.warning : C.danger,
                }}>{g.grade}</span>
              </div>
              <p style={{ fontSize:'12px', color:C.textSecondary }}>{g.score}/100</p>
            </div>
          ))}
          <button
            onClick={()=>navigate('/admin/grades')}
            style={{ marginTop:'12px', background:'none', border:'none', color:C.info, fontSize:'12px', fontWeight:600, cursor:'pointer', padding:0 }}
          >
            {E?'View all grades →':'Lihat semua gred →'}
          </button>
        </Card>

        {/* Next class */}
        <Card style={{ padding:'20px' }}>
          <p style={{ fontSize:'11px', fontWeight:600, color:C.textSecondary, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'12px' }}>
            {E?'Next Class':'Kelas Seterusnya'}
          </p>
          <p style={{ fontFamily:T.font.serif, fontSize:'18px', fontWeight:600, color:C.textPrimary, marginBottom:'4px' }}>
            {child.nextClass.subject}
          </p>
          <p style={{ fontSize:'12px', color:C.textSecondary, marginBottom:'12px' }}>
            {child.nextClass.time} · {E?'Room':'Bilik'} {child.nextClass.room}
          </p>
          <button
            onClick={()=>navigate('/admin/timetable')}
            style={{ background:'none', border:'none', color:C.info, fontSize:'12px', fontWeight:600, cursor:'pointer', padding:0 }}
          >
            {E?'Full timetable →':'Jadual penuh →'}
          </button>
        </Card>

        {/* Notifications */}
        <Card style={{ padding:'20px' }}>
          <p style={{ fontSize:'11px', fontWeight:600, color:C.textSecondary, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'12px' }}>
            {E?'Notifications':'Pemberitahuan'}
          </p>
          <div style={{ display:'flex', alignItems:'flex-start', gap:'8px', marginBottom:'12px' }}>
            <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:C.royal, marginTop:'4px', flexShrink:0 }}/>
            <p style={{ fontSize:'12px', color:C.textPrimary, lineHeight:1.5 }}>
              {E?'School circular: Exam schedule for Term 2':'Surat pekeliling: Jadual peperiksaan Penggal 2'}
            </p>
          </div>
          <button
            onClick={()=>navigate('/admin/attendance/alerts')}
            style={{ background:'none', border:'none', color:C.info, fontSize:'12px', fontWeight:600, cursor:'pointer', padding:0 }}
          >
            {E?'View all →':'Lihat semua →'}
          </button>
        </Card>
      </div>
    </>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export function DashboardPage({ user }: { user:AuthUser }) {
  const { lang } = useLang();
  const navigate  = useNavigate();
  const role      = primaryRole(user);
  const E = lang==='en';

  const isSuper   = role==='super_admin' || role==='moe_admin';
  const isAdmin   = role==='admin' || role==='principal';
  const isTeacher = role==='teacher';
  const isParent  = role==='parent';
  const isDpe     = role==='dpe_officer';
  const isMora    = role==='mora_officer';

  const greeting = (() => {
    const h = new Date().getHours();
    if (E) return h<12?'Good morning':h<17?'Good afternoon':'Good evening';
    return h<12?'Selamat pagi':h<17?'Selamat tengah hari':'Selamat petang';
  })();

  const displayName = user.email.split('@')[0] ?? '';
  const pageTitle = E ? 'Dashboard' : 'Papan Pemuka';
  usePageTitle(pageTitle);

  return (
    <>
      {/* Welcome header */}
      <div style={{ marginBottom:'24px' }}>
        <h1 style={{
          fontFamily:T.font.serif, fontSize:'28px', fontWeight:700,
          color:C.textPrimary, marginBottom:'4px', lineHeight:1.2,
          letterSpacing:'-0.01em',
        }}>
          {greeting}, {displayName}
        </h1>
        <p style={{ fontSize:'13px', color:C.textSecondary }}>
          {new Date().toLocaleDateString(E?'en-GB':'ms-BN', {
            weekday:'long', year:'numeric', month:'long', day:'numeric',
            timeZone:'Asia/Brunei',
          })}
          {' · '}
          <span style={{ color:C.textPrimary, fontWeight:500 }}>
            {E?'Academic Year 2026':'Tahun Akademik 2026'}
          </span>
        </p>
      </div>

      {/* Role dashboards */}
      {isSuper   && <MOEDashboard lang={lang} />}
      {isAdmin   && <SchoolAdminDashboard lang={lang} navigate={navigate} />}
      {isTeacher && <TeacherDashboard lang={lang} navigate={navigate} />}
      {isParent  && <ParentDashboard lang={lang} navigate={navigate} />}
      {(isDpe || isMora) && (
        <Card style={{ padding:'32px', textAlign:'center' }}>
          <Bot size={32} color={C.textDisabled} style={{ marginBottom:'12px' }}/>
          <h2 style={{ fontFamily:T.font.serif, fontSize:'20px', fontWeight:600, color:C.textPrimary, marginBottom:'8px' }}>
            {E?'Welcome to SERPS':'Selamat datang ke SERPS'}
          </h2>
          <p style={{ fontSize:'13px', color:C.textSecondary, maxWidth:'400px', margin:'0 auto 20px' }}>
            {isDpe
              ? (E?'Access private school inspections, licenses, and approvals from the sidebar.':'Akses pemeriksaan, lesen, dan kelulusan sekolah swasta dari bar sisi.')
              : (E?'Access MORA school records from the sidebar.':'Akses rekod sekolah MORA dari bar sisi.')}
          </p>
          <button
            onClick={()=>navigate(isDpe?'/admin/dpe':'/admin/mora')}
            style={{ background:C.royal, color:C.midnight, border:'none', borderRadius:'8px', padding:'10px 24px', fontSize:'13px', fontWeight:700, cursor:'pointer', fontFamily:T.font.sans }}
          >
            {E?'Get Started →':'Mulakan →'}
          </button>
        </Card>
      )}
      {!isSuper && !isAdmin && !isTeacher && !isParent && !isDpe && !isMora && (
        <Card style={{ padding:'40px', textAlign:'center' }}>
          <p style={{ color:C.textSecondary, fontSize:'14px' }}>
            {E?'Welcome. Your dashboard is being configured.':'Selamat datang. Papan pemuka anda sedang dikonfigurasi.'}
          </p>
        </Card>
      )}
    </>
  );
}
